/**
 * Activity Cache Manager
 * 
 * Manages in-memory cache of location activity events with:
 * - localStorage persistence (debounced every 2 seconds)
 * - Automatic midnight reset
 * - FIFO eviction when cache is full
 * - Query API for filtering by location, rack, time window
 * - Cache validation and restoration on page load
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 5 Implementation
 */

import { LocationActivityEvent } from '../../../../types/locationActivity';
import * as localStorageHelper from '../../../../utils/localStorageHelper';

/**
 * Configuration for ActivityCacheManager
 */
export interface CacheConfig {
  /** Maximum number of events to cache */
  maxCacheSize: number;
  
  /** Enable persistence to localStorage */
  enablePersistence: boolean;
  
  /** Debounce delay for localStorage writes (milliseconds) */
  persistenceDebounceMs: number;
  
  /** Enable automatic reset at midnight */
  enableMidnightReset: boolean;
  
  /** Enable statistics tracking */
  enableStats: boolean;
}

/**
 * Time window for filtering events
 */
export enum TimeWindow {
  Last1Hour = '1h',
  Last4Hours = '4h',
  Last8Hours = '8h',
  Today = 'today'
}

/**
 * Cache statistics
 */
export interface CacheStats {
  /** Total events currently in cache */
  totalEvents: number;
  
  /** Cache size limit */
  maxSize: number;
  
  /** Percentage of cache used */
  usagePercentage: number;
  
  /** Oldest event timestamp */
  oldestEventTimestamp?: string;
  
  /** Newest event timestamp */
  newestEventTimestamp?: string;
  
  /** Cache age (time since oldest event) in seconds */
  cacheAgeSeconds?: number;
  
  /** Total events added (lifetime) */
  totalEventsAdded: number;
  
  /** Total events evicted (lifetime) */
  totalEventsEvicted: number;
  
  /** Total events dropped (rejected when cache full) */
  totalEventsDropped: number;
  
  /** Last save to localStorage timestamp */
  lastPersistedAt?: string;
  
  /** Number of times restored from localStorage */
  restoredFromStorageCount: number;
}

/**
 * Query result from cache
 */
export interface QueryResult {
  /** Matching events */
  events: LocationActivityEvent[];
  
  /** Total matches */
  totalCount: number;
  
  /** Query execution time (milliseconds) */
  executionTimeMs: number;
}

/**
 * Saved cache data structure for localStorage
 */
interface CachedData {
  /** Cache version for compatibility checking */
  version: string;
  
  /** Events array */
  events: LocationActivityEvent[];
  
  /** Timestamp when saved */
  savedAt: string;
  
  /** Lifetime statistics */
  stats: {
    totalEventsAdded: number;
    totalEventsEvicted: number;
    totalEventsDropped: number;
  };
}

/**
 * Default configuration
 */
const DEFAULT_CONFIG: CacheConfig = {
  maxCacheSize: 10000,
  enablePersistence: true,
  persistenceDebounceMs: 2000, // 2 seconds
  enableMidnightReset: true,
  enableStats: true
};

/**
 * localStorage key for cached events
 */
const CACHE_STORAGE_KEY = 'bizclick_location_activity_cache';

/**
 * Current cache version (for compatibility)
 */
const CACHE_VERSION = '1.0';

/**
 * Activity Cache Manager
 * Manages event cache with persistence and automatic cleanup
 */
export class ActivityCacheManager {
  private config: CacheConfig;
  private events: LocationActivityEvent[] = [];
  private stats: CacheStats;
  private debouncedSave: (() => void) | null = null;
  private midnightResetTimeout: NodeJS.Timeout | null = null;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Initialize statistics
    this.stats = {
      totalEvents: 0,
      maxSize: this.config.maxCacheSize,
      usagePercentage: 0,
      totalEventsAdded: 0,
      totalEventsEvicted: 0,
      totalEventsDropped: 0,
      restoredFromStorageCount: 0
    };

    // Setup debounced save function
    if (this.config.enablePersistence) {
      this.debouncedSave = localStorageHelper.debounce(
        () => this.saveToLocalStorage(),
        this.config.persistenceDebounceMs
      );
    }

    // Restore from localStorage on initialization
    this.restoreFromLocalStorage();

    // Schedule midnight reset
    if (this.config.enableMidnightReset) {
      this.scheduleMidnightReset();
    }

    console.log('[ActivityCacheManager] Initialized with config:', this.config);
    console.log('[ActivityCacheManager] Restored', this.events.length, 'events from localStorage');
  }

  /**
   * Add event to cache
   * 
   * @param event - Activity event to add
   * @returns Success boolean
   */
  public addEvent(event: LocationActivityEvent): boolean {
    // Check if cache is full
    if (this.events.length >= this.config.maxCacheSize) {
      // FIFO eviction - remove oldest event
      const evicted = this.events.shift();
      
      if (this.config.enableStats && evicted) {
        this.stats.totalEventsEvicted++;
        console.log(
          '[ActivityCacheManager] Cache full - evicted oldest event:',
          evicted.binCode,
          'at',
          evicted.timestamp
        );
      }
    }

    // Add new event
    this.events.push(event);

    // Update statistics
    if (this.config.enableStats) {
      this.stats.totalEventsAdded++;
      this.updateCacheStats();
    }

    // Trigger debounced save to localStorage
    if (this.debouncedSave) {
      this.debouncedSave();
    }

    return true;
  }

  /**
   * Add multiple events to cache
   * 
   * @param events - Array of events to add
   * @returns Number of events successfully added
   */
  public addEvents(events: LocationActivityEvent[]): number {
    let addedCount = 0;
    
    for (const event of events) {
      if (this.addEvent(event)) {
        addedCount++;
      }
    }

    return addedCount;
  }

  /**
   * Query events by location
   * 
   * @param binCode - Location code to filter by
   * @param timeWindow - Optional time window filter
   * @returns Query result
   */
  public queryByLocation(binCode: string, timeWindow?: TimeWindow): QueryResult {
    const startTime = performance.now();
    
    let filtered = this.events.filter(event => event.binCode === binCode);
    
    // Apply time window filter if specified
    if (timeWindow) {
      filtered = this.filterByTimeWindow(filtered, timeWindow);
    }

    const executionTimeMs = performance.now() - startTime;

    return {
      events: filtered,
      totalCount: filtered.length,
      executionTimeMs
    };
  }

  /**
   * Query events by rack number
   * 
   * @param rackNumber - Rack number to filter by
   * @param timeWindow - Optional time window filter
   * @returns Query result
   */
  public queryByRack(rackNumber: number, timeWindow?: TimeWindow): QueryResult {
    const startTime = performance.now();
    
    let filtered = this.events.filter(event => event.rackNumber === rackNumber);
    
    // Apply time window filter if specified
    if (timeWindow) {
      filtered = this.filterByTimeWindow(filtered, timeWindow);
    }

    const executionTimeMs = performance.now() - startTime;

    return {
      events: filtered,
      totalCount: filtered.length,
      executionTimeMs
    };
  }

  /**
   * Query events by time window
   * 
   * @param timeWindow - Time window to filter by
   * @returns Query result
   */
  public queryByTimeWindow(timeWindow: TimeWindow): QueryResult {
    const startTime = performance.now();
    
    const filtered = this.filterByTimeWindow(this.events, timeWindow);

    const executionTimeMs = performance.now() - startTime;

    return {
      events: filtered,
      totalCount: filtered.length,
      executionTimeMs
    };
  }

  /**
   * Get all events (no filtering)
   * 
   * @returns All cached events
   */
  public getAllEvents(): LocationActivityEvent[] {
    return [...this.events]; // Return copy to prevent external mutation
  }

  /**
   * Get cache statistics
   * 
   * @returns Current cache statistics
   */
  public getStats(): CacheStats {
    this.updateCacheStats();
    return { ...this.stats }; // Return copy
  }

  /**
   * Clear all events from cache
   * 
   * @param persist - If true, also clear localStorage
   */
  public clear(persist: boolean = true): void {
    this.events = [];
    
    if (persist && this.config.enablePersistence) {
      localStorageHelper.removeItem(CACHE_STORAGE_KEY);
      console.log('[ActivityCacheManager] Cache cleared and localStorage removed');
    } else {
      console.log('[ActivityCacheManager] Cache cleared (localStorage preserved)');
    }

    this.updateCacheStats();
  }

  /**
   * Manually trigger save to localStorage (bypassing debounce)
   */
  public forceSave(): void {
    if (this.config.enablePersistence) {
      this.saveToLocalStorage();
    }
  }

  /**
   * Cleanup - call before component unmount
   */
  public cleanup(): void {
    // Force save before cleanup
    this.forceSave();

    // Clear midnight reset timeout
    if (this.midnightResetTimeout) {
      clearTimeout(this.midnightResetTimeout);
      this.midnightResetTimeout = null;
    }

    console.log('[ActivityCacheManager] Cleanup completed');
  }

  /**
   * Filter events by time window
   * 
   * @param events - Events to filter
   * @param timeWindow - Time window
   * @returns Filtered events
   */
  private filterByTimeWindow(
    events: LocationActivityEvent[],
    timeWindow: TimeWindow
  ): LocationActivityEvent[] {
    const now = new Date();
    let cutoffTime: Date;

    switch (timeWindow) {
      case TimeWindow.Last1Hour:
        cutoffTime = new Date(now.getTime() - 60 * 60 * 1000);
        break;
      case TimeWindow.Last4Hours:
        cutoffTime = new Date(now.getTime() - 4 * 60 * 60 * 1000);
        break;
      case TimeWindow.Last8Hours:
        cutoffTime = new Date(now.getTime() - 8 * 60 * 60 * 1000);
        break;
      case TimeWindow.Today:
        cutoffTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
        break;
      default:
        return events; // No filtering
    }

    return events.filter(event => {
      const eventTime = new Date(event.timestamp);
      return eventTime >= cutoffTime;
    });
  }

  /**
   * Update cache statistics
   */
  private updateCacheStats(): void {
    this.stats.totalEvents = this.events.length;
    this.stats.usagePercentage = (this.events.length / this.config.maxCacheSize) * 100;

    if (this.events.length > 0) {
      // Find oldest and newest events
      const timestamps = this.events.map(e => new Date(e.timestamp).getTime());
      const oldestTime = Math.min(...timestamps);
      const newestTime = Math.max(...timestamps);

      this.stats.oldestEventTimestamp = new Date(oldestTime).toISOString();
      this.stats.newestEventTimestamp = new Date(newestTime).toISOString();
      this.stats.cacheAgeSeconds = (newestTime - oldestTime) / 1000;
    } else {
      this.stats.oldestEventTimestamp = undefined;
      this.stats.newestEventTimestamp = undefined;
      this.stats.cacheAgeSeconds = undefined;
    }
  }

  /**
   * Save cache to localStorage
   */
  private saveToLocalStorage(): void {
    const data: CachedData = {
      version: CACHE_VERSION,
      events: this.events,
      savedAt: new Date().toISOString(),
      stats: {
        totalEventsAdded: this.stats.totalEventsAdded,
        totalEventsEvicted: this.stats.totalEventsEvicted,
        totalEventsDropped: this.stats.totalEventsDropped
      }
    };

    const success = localStorageHelper.setItem(CACHE_STORAGE_KEY, data);
    
    if (success) {
      this.stats.lastPersistedAt = data.savedAt;
      console.log(
        '[ActivityCacheManager] Saved',
        this.events.length,
        'events to localStorage'
      );
    } else {
      console.warn('[ActivityCacheManager] Failed to save to localStorage');
    }
  }

  /**
   * Restore cache from localStorage
   */
  private restoreFromLocalStorage(): void {
    const data = localStorageHelper.getItem<CachedData>(CACHE_STORAGE_KEY);

    if (!data) {
      console.log('[ActivityCacheManager] No cached data found in localStorage');
      return;
    }

    // Validate cache data structure
    if (!localStorageHelper.validateCacheData(data)) {
      console.warn('[ActivityCacheManager] Invalid cache data format, ignoring');
      localStorageHelper.removeItem(CACHE_STORAGE_KEY);
      return;
    }

    // Check if cache is from today
    if (!localStorageHelper.isCacheFromToday(data.savedAt)) {
      console.log(
        '[ActivityCacheManager] Cache is from previous day, not restoring',
        'Saved at:', data.savedAt
      );
      localStorageHelper.removeItem(CACHE_STORAGE_KEY);
      return;
    }

    // Check version compatibility
    if (data.version !== CACHE_VERSION) {
      console.warn(
        '[ActivityCacheManager] Cache version mismatch:',
        data.version,
        'vs',
        CACHE_VERSION,
        '- not restoring'
      );
      localStorageHelper.removeItem(CACHE_STORAGE_KEY);
      return;
    }

    // Restore events
    this.events = data.events;

    // Restore statistics
    if (data.stats) {
      this.stats.totalEventsAdded = data.stats.totalEventsAdded || 0;
      this.stats.totalEventsEvicted = data.stats.totalEventsEvicted || 0;
      this.stats.totalEventsDropped = data.stats.totalEventsDropped || 0;
    }

    this.stats.restoredFromStorageCount++;

    this.updateCacheStats();

    console.log(
      '[ActivityCacheManager] Restored',
      this.events.length,
      'events from localStorage',
      '(saved at', data.savedAt + ')'
    );
  }

  /**
   * Schedule automatic cache reset at midnight
   */
  private scheduleMidnightReset(): void {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
    const msUntilMidnight = tomorrow.getTime() - now.getTime();

    console.log(
      '[ActivityCacheManager] Scheduling midnight reset in',
      Math.floor(msUntilMidnight / 1000 / 60),
      'minutes'
    );

    this.midnightResetTimeout = setTimeout(() => {
      console.log('[ActivityCacheManager] Midnight reset triggered - clearing cache');
      console.log('[ActivityCacheManager] Cached events before reset:', this.events.length);
      
      // Clear cache
      this.clear(true);

      // Reset lifetime statistics
      this.stats.totalEventsAdded = 0;
      this.stats.totalEventsEvicted = 0;
      this.stats.totalEventsDropped = 0;

      // Schedule next midnight reset
      this.scheduleMidnightReset();
    }, msUntilMidnight);
  }
}
